import moment from "moment-timezone";
import { useEffect } from "react";
import { Link, Redirect } from "react-router-dom";
import { SettingsButton } from "src/components/buttons/SettingsButton";
import { GrayIcon } from "src/components/icons/GrayIcon";
import Breadcrumbs from "src/components/layout/Breadcrumbs";
import Container from "src/components/layout/Container";
import { SettingOnboardsProgress } from "src/components/settings/SettingOnboardProgress";
import { useStore } from "src/stores/Store";
import message from "src/utils/message";
import { useListBackground } from "src/utils/setListBackground";
import {
  BetaAccessType,
  PermissionType,
  StripeType,
} from "types/code-generator";

interface Props {}

export default function Settings(props: Props) {
  const store = useStore();
  useListBackground();

  useEffect(() => {
    if (
      !store.user?.permissions.includes(PermissionType.AccessAdvancedSetting) &&
      !store.user.isSuperAdmin
    ) {
      message.error("Unauthorized");
    }
  }, []);

  const department = store.organization.departments.find((d) => d.default);
  let infoRequests;
  let programs;
  let tags;
  let filters;
  let billingCategories;
  let discounts;
  let coupons;
  let addons;

  if (!store.organization.hasDepartments) {
    infoRequests = (
      <SettingsButton
        to={`/admin/settings/${department.id}/student-info-requests`}
        title="Info Requests"
        icon={<i className="far fa-circle-info" />}
      >
        Non-critical information, like insurance info & t-shirt sizes.
      </SettingsButton>
    );

    programs = (
      <SettingsButton
        to={`/admin/settings/${department.id}/programs`}
        title="Programs"
        icon={<i className="far fa-sitemap" />}
      >
        Categories of things you offer for registration.
      </SettingsButton>
    );

    tags = (
      <SettingsButton
        to={`/admin/settings/${department.id}/tags`}
        title="Tags"
        icon={<i className="far fa-tags" />}
      >
        An extra way to filter your offerings.
      </SettingsButton>
    );

    filters = (
      <SettingsButton
        to={`/admin/settings/${department.id}/filters`}
        title="Public Filtering"
        icon={<i className="far fa-filter" />}
      >
        Keep registration simple for your customers.
      </SettingsButton>
    );

    billingCategories = (
      <SettingsButton
        to={`/admin/settings/${department.id}/billing-categories`}
        title="Billing Categories"
        icon={<i className="far fa-file-invoice-dollar" />}
      >
        Internally group transactions for your reporting.
      </SettingsButton>
    );

    discounts = (
      <SettingsButton
        to={`/admin/settings/${department.id}/discounts`}
        title="Discounts"
        icon={<i className="far fa-hand-holding-dollar" />}
      >
        Auto-apply multi-participant and multi-registration discounts.
      </SettingsButton>
    );

    coupons = (
      <SettingsButton
        to={`/admin/settings/${department.id}/coupons`}
        title="Coupons"
        icon={<i className="far fa-gift-card" />}
      >
        Add codes and details.
      </SettingsButton>
    );

    addons = (
      <SettingsButton
        to={`/admin/settings/${department.id}/addons`}
        title="Products, Add-ons"
        icon={<i className="far fa-cash-register" />}
      >
        Up-sell anything during registrations & in {store.organization.title}{" "}
        Store.
      </SettingsButton>
    );
  }

  const showDepartments =
    store.organization.betaAccess.includes(BetaAccessType.Departments) &&
    (store.user.permissions.includes(PermissionType.ManageDepartment) ||
      store.user.isSalesOrAbove);
  const hasEditedDepartments = store.organization.departments.some((d) =>
    moment(d.updatedAt).isAfter(store.organization.createdAt)
  );
  const listDepartments =
    store.organization.hasDepartments && hasEditedDepartments;
  const today = moment();
  const organizationUpdated = today.diff(
    moment(store.organization.updatedAt),
    "days"
  );
  const stripeConnected = Boolean(
    store.organization.stripeAccountId ||
      store.organization.stripeExpressAccountId
  );

  if (
    !store.user?.permissions.includes(PermissionType.AccessAdvancedSetting) &&
    !store.user.isSuperAdmin
  ) {
    return <Redirect to="/admin" />;
  }

  return (
    <>
      <Container full main className="bg-white">
        <div className="border-b border-borderGray px-4 pb-4">
          <Breadcrumbs>
            <Link to="/admin">Home</Link> / <strong>Settings & Reports</strong>
          </Breadcrumbs>

          <h1 className="mb-0">Settings</h1>
        </div>

        <div className="bg-gradient-to-b from-paleAction to-white">
          <Container large className="pb-12 pt-8">
            <SettingOnboardsProgress />

            <div className="mb-8">
              <h2>Your Brand</h2>

              <div className="grid grid-cols-4 gap-4 sm:grid-cols-1">
                <SettingsButton
                  to={`/admin/account`}
                  title="General"
                  icon={<i className="far fa-globe" />}
                >
                  Your organization details and social media links.
                  <div className="pt-1 text-xs text-empty">
                    <i className="far fa-check text-success" /> Modified{" "}
                    {organizationUpdated} day{organizationUpdated !== 1 && "s"}{" "}
                    ago
                  </div>
                </SettingsButton>

                <SettingsButton
                  to={`/admin/logo`}
                  title="Logo"
                  icon={
                    <img
                      src="/img/captyn-icon.png"
                      alt="Captyn Logo"
                      className="h-6 w-6"
                    />
                  }
                >
                  White label Captyn to your brand.
                </SettingsButton>

                <SettingsButton
                  to={`/admin/brand`}
                  title="Brand Color"
                  icon={
                    <div
                      className="h-5 w-5 rounded-full"
                      style={{ backgroundColor: store.theme.orgOrgColor }}
                    />
                  }
                >
                  Registration buttons and theming are your choice.
                </SettingsButton>

                <SettingsButton
                  to={`/admin/terminology`}
                  title="Terminology"
                  icon={<i className="far fa-person-sign" />}
                >
                  Language matters — we adapt to your terminology.
                </SettingsButton>
              </div>
            </div>

            <div className="mb-8">
              <h2>Your Customer Information</h2>

              <div className="grid grid-cols-4 gap-4 sm:grid-cols-1">
                <SettingsButton
                  to={`/admin/required-fields`}
                  title="Required Fields"
                  icon={<i className="far fa-asterisk" />}
                >
                  Address, gender, birthdate, emergency contact, & photo.
                </SettingsButton>

                <SettingsButton
                  to={`/admin/health-questions`}
                  title="Health Concerns"
                  icon={<i className="far fa-suitcase-medical" />}
                >
                  Critical information needed to avoid an emergency.
                </SettingsButton>

                <SettingsButton
                  to={`/admin/agreements`}
                  title="Agreements"
                  icon={<i className="far fa-file-signature" />}
                >
                  Add your liability waivers, photo releases, etc.
                </SettingsButton>

                {infoRequests}
              </div>
            </div>

            <div className="mb-8">
              <h2>Your Operations</h2>

              <div className="grid grid-cols-4 gap-4 sm:grid-cols-1">
                <SettingsButton
                  to={`/admin/locations`}
                  title="Locations"
                  icon={<i className="far fa-map-location" />}
                >
                  Where you do business.
                </SettingsButton>

                {programs}

                {tags}

                {store.organization.paymentDisplay === StripeType.Standard && (
                  <SettingsButton
                    to={`/admin/bank`}
                    title="Stripe Connection"
                    icon={
                      <img
                        src="/img/stripe-s-brands.svg"
                        className="h-4 w-4"
                        alt="Stripe"
                      />
                    }
                  >
                    Get next-day payouts.
                    {stripeConnected && (
                      <div className="pt-1 text-xs text-empty">
                        <i className="far fa-check text-success" /> Connected
                      </div>
                    )}
                  </SettingsButton>
                )}

                {store.organization.paymentDisplay === StripeType.Express && (
                  <SettingsButton
                    to={`/admin/bank`}
                    title="Bank Connections"
                    icon={
                      <i className="far fa-building-columns text-organization" />
                    }
                  >
                    Get next-day payouts.
                    {stripeConnected && (
                      <div className="pt-1 text-xs text-empty">
                        <i className="far fa-check text-success" /> Connected
                      </div>
                    )}
                  </SettingsButton>
                )}

                {store.organization.paymentDisplay === StripeType.Adyen && (
                  <SettingsButton
                    to={`/admin/bank`}
                    title="Bank Connections"
                    icon={
                      <i className="far fa-building-columns text-organization" />
                    }
                  >
                    Get next-day payouts.
                    {stripeConnected && (
                      <div className="pt-1 text-xs text-empty">
                        <i className="far fa-check text-success" /> Connected
                      </div>
                    )}
                  </SettingsButton>
                )}

                <SettingsButton
                  to={`/admin/communications`}
                  title="Communications"
                  icon={<i className="far fa-megaphone" />}
                >
                  View sent emails & texts, and set up templates to send
                  automatically.
                </SettingsButton>

                {filters}

                {billingCategories}
              </div>
            </div>

            <div className="mb-8">
              <h2>Customer Checkout Options</h2>
              <div className="grid grid-cols-4 gap-4 sm:grid-cols-1">
                {discounts}

                {coupons}

                {addons}

                <SettingsButton
                  to={`/admin/fees`}
                  title="Line Item Fees"
                  icon={<i className="far fa-badge-dollar" />}
                >
                  When you need to add sales tax or other fees to certain
                  offerings.
                </SettingsButton>
              </div>
            </div>

            {showDepartments && (
              <div className="mb-8">
                <h2>Departments</h2>
                <div className="grid grid-cols-4 gap-4 sm:grid-cols-1">
                  <SettingsButton
                    to="/admin/departments"
                    title="Dept. Setup"
                    icon={<i aria-hidden className="far fa-boxes-stacked" />}
                  >
                    Large organizations that need to segment operations.
                  </SettingsButton>

                  {listDepartments &&
                    store.organization.departments
                      .filter((d) => {
                        if (store.user.isSalesOrAbove) {
                          return true;
                        }
                        return store.user.departments.find(
                          (ud) => ud.id === d.id
                        );
                      })
                      .sort(
                        (a, b) =>
                          new Date(a.createdAt).getTime() -
                          new Date(b.createdAt).getTime()
                      )
                      .map((d) => {
                        return (
                          <SettingsButton
                            to={`/admin/settings/${d.id}`}
                            title={d.title}
                            key={d.id}
                            department
                          >
                            <div className="mb-1.5 flex pt-1">
                              <GrayIcon className="mr-2">
                                <i className="far fa-id-card" />
                              </GrayIcon>
                              <div className="flex-1 truncate">
                                {d.contactName}
                              </div>
                            </div>
                            <div className="flex">
                              <GrayIcon className="mr-2">
                                <i className="far fa-at" />
                              </GrayIcon>
                              <div className="flex-1 truncate">{d.email}</div>
                            </div>
                          </SettingsButton>
                        );
                      })}
                </div>
              </div>
            )}

            <div className="pb-8">
              <h2>Reports</h2>
              <div className="grid grid-cols-4 gap-4 sm:grid-cols-1">
                <SettingsButton
                  to="/admin/transactions"
                  title="Financial"
                  icon={<i aria-hidden className="far fa-file-chart-column" />}
                >
                  View all transactions and invoice reports for any date range
                  or payment page.
                </SettingsButton>
              </div>
            </div>
          </Container>
        </div>
      </Container>
    </>
  );
}
